import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Bell, AlertTriangle, Package, Clock, X } from 'lucide-react';

const SEVERITY_ICONS = {
  critical: { icon: AlertTriangle, color: 'text-rose-500' },
  warning: { icon: Package, color: 'text-amber-500' },
  info: { icon: Clock, color: 'text-sky-500' },
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [criticalCount, setCriticalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pharmxai_dismissed_notifs') || '[]'); }
    catch { return []; }
  });

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      const all = res.data.notifications || [];
      const visible = all.filter(n => !dismissed.includes(n.id));
      setNotifications(visible);
      setCriticalCount(visible.filter(n => n.severity === 'critical').length);
    } catch (e) {
      // Silent fail — a failed notification poll shouldn't disrupt the UI.
      console.error('Notification poll failed:', e);
    }
  }, [dismissed]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const dismiss = (id) => {
    const updated = [...dismissed, id];
    setDismissed(updated);
    localStorage.setItem('pharmxai_dismissed_notifs', JSON.stringify(updated));
    setNotifications(prev => prev.filter(n => n.id !== id));
    setCriticalCount(prev => {
      const n = notifications.find(x => x.id === id);
      return n?.severity === 'critical' ? prev - 1 : prev;
    });
  };

  const totalCount = notifications.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9 relative" data-testid="notification-bell">
          <Bell className="h-4 w-4" />
          {totalCount > 0 && (
            <span className={`absolute top-0 right-0 h-4 min-w-4 flex items-center justify-center rounded-full text-[10px] font-bold px-1 ${
              criticalCount > 0 ? 'bg-destructive text-destructive-foreground' : 'bg-amber-500 text-white'
            }`} data-testid="notification-count">
              {totalCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b border-border flex items-center justify-between">
          <p className="text-sm font-semibold">Notifications</p>
          {totalCount > 0 && (
            <Badge variant="secondary" className="text-xs">{totalCount}</Badge>
          )}
        </div>
        <ScrollArea className="max-h-[360px]">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              <Bell className="h-6 w-6 mx-auto mb-2 opacity-30" />
              <p className="text-sm">All clear!</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map(notif => {
                const cfg = SEVERITY_ICONS[notif.severity] || SEVERITY_ICONS.info;
                const target = notif.type === 'adr_severe' ? '/customers/reactions'
                  : notif.type === 'checkout_review' ? '/customers/triage'
                  : notif.type === 'duti_action' ? '/duti-ai'
                  : notif.type === 'patient_document' ? `/customers/records?patient=${notif.patient_user_id}&tab=documents`
                  : notif.type === 'recall_lock' ? `/inventory/${notif.product_id}`
                  : notif.type === 'fda_reminder' ? '/customers/reactions'
                  : notif.type === 'password_reset_request' ? '/users'
                  : notif.type === 'sms_failure' ? '/sms-log?status=failed' : notif.link || null;
                const clickable = !!target;
                return (
                  <div key={notif.id} className={`p-3 hover:bg-muted/50 transition-colors flex gap-2.5 ${clickable ? 'cursor-pointer' : ''}`}
                    onClick={clickable ? () => { setOpen(false); navigate(target); } : undefined}
                    data-testid={`notif-${notif.id}`}>
                    <cfg.icon className={`h-4 w-4 mt-0.5 shrink-0 ${cfg.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold">{notif.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); dismiss(notif.id); }}
                      className="h-5 w-5 rounded hover:bg-muted flex items-center justify-center shrink-0"
                      data-testid={`dismiss-${notif.id}`}
                    >
                      <X className="h-3 w-3 text-muted-foreground" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
